/**
 * reportingConfigService (A1, 2026-09-01) — the single source of truth for the numbers reports
 * were previously HARD-CODING (8.25% tax, 3% fee, estimate payment ratios). Every report reads these
 * so the P&L reconciles with itself. Stored at shopSettings/reporting via the generic settings store.
 * BEHAVIOR-PIN: SLIK-REPORTING-OVERHAUL-OVERNIGHT-2026-09-01
 */
import { shopSettingsService } from './shopSettingsService';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';

export interface ReportingConfig {
  /** Tax rate applied to SERVICE revenue (fraction). NJ haircuts untaxed → 0. Matches Finance Summary. */
  serviceTaxRate: number;
  /** Tax rate applied to PRODUCT revenue (fraction). Finance Summary uses product 9% → 0.09. */
  productTaxRate: number;
  /** Card processing fee (fraction) used when real Stripe fee data is unavailable. */
  processingFeePct: number;
  /** 'real' = derive payment-method split from actual checkout/Stripe data; 'estimate' = fallback ratios. */
  paymentSplitSource: 'real' | 'estimate';
  /** Fallback COGS as a fraction of product revenue when a product has no wholesale price set. */
  defaultProductCogsPct: number;
}

export const DEFAULT_REPORTING_CONFIG: ReportingConfig = {
  serviceTaxRate: 0,
  productTaxRate: 0.09,
  processingFeePct: 0.029,
  paymentSplitSource: 'real',
  defaultProductCogsPct: 0.5,
};

const TYPE = 'reporting';

let _cache: ReportingConfig | null = null;

// H18 FIX (Jose 2026-10-29 audit): the config was cached after first read with no live listener, so a
// tax/processing-fee/COGS change made on ANOTHER device (or another tab) stayed STALE in this session
// until something passed force=true — reports on different devices could reconcile to different rates.
// A live snapshot on the backing doc (shopSettings/reporting — same doc getSettingsByType reads) keeps
// _cache fresh everywhere, self-heals on auth-ready, and logs (never silent). Source of truth unchanged.
let _liveUnsub: (() => void) | null = null;
function startReportingConfigLive(): void {
  try { _liveUnsub?.(); } catch { /* ignore */ }
  try {
    _liveUnsub = onSnapshot(doc(db, 'shopSettings', TYPE), (snap) => {
      if (snap.exists()) _cache = { ...DEFAULT_REPORTING_CONFIG, ...(snap.data() as Partial<ReportingConfig>) };
    }, (err) => console.warn('[reportingConfig] live subscription error (will retry on auth change):', (err as any)?.message || err));
  } catch { /* ignore */ }
}
try { onAuthStateChanged(auth, (u) => { if (u) startReportingConfigLive(); }); } catch { /* ignore */ }

/** Read the config (defaults merged). Cached after first read; pass force to refetch. */
export async function getReportingConfig(force = false): Promise<ReportingConfig> {
  if (_cache && !force) return _cache;
  try {
    const stored = await shopSettingsService.getSettingsByType<Partial<ReportingConfig>>(TYPE);
    _cache = { ...DEFAULT_REPORTING_CONFIG, ...(stored || {}) };
  } catch {
    _cache = { ...DEFAULT_REPORTING_CONFIG };
  }
  return _cache;
}

/** Synchronous best-effort accessor for hot paths — returns the last-read config or defaults. */
export function getReportingConfigSync(): ReportingConfig {
  return _cache || { ...DEFAULT_REPORTING_CONFIG };
}

export async function saveReportingConfig(config: ReportingConfig): Promise<void> {
  await shopSettingsService.saveSettingsByType<ReportingConfig>(TYPE, config);
  _cache = { ...config };
}
